import { nanoid } from 'nanoid';

import type { AdvancedEditCut } from '../../../../../domain';

const sortCuts = (cuts: AdvancedEditCut[]): AdvancedEditCut[] =>
  [...cuts].sort((left, right) => left.start_time_ms - right.start_time_ms);

const isValidRange = (startMs: number, endMs: number): boolean =>
  startMs < endMs;

const finalizeCuts = (cuts: AdvancedEditCut[]): AdvancedEditCut[] =>
  sortCuts(cuts).filter(cut =>
    isValidRange(cut.start_time_ms, cut.end_time_ms),
  );

/**
 * Removes a time range from the active cuts.
 * Overlapping cuts are trimmed or split, fully covered cuts are removed.
 */
export const deleteRangeFromCuts = (
  cuts: AdvancedEditCut[],
  startMs: number,
  endMs: number,
): AdvancedEditCut[] => {
  if (!isValidRange(startMs, endMs)) {
    return sortCuts(cuts);
  }

  const nextCuts: AdvancedEditCut[] = [];

  for (const cut of sortCuts(cuts)) {
    const cutStart = cut.start_time_ms;
    const cutEnd = cut.end_time_ms;

    // No overlap.
    if (cutEnd <= startMs || cutStart >= endMs) {
      nextCuts.push(cut);
      continue;
    }

    // Left remainder.
    if (cutStart < startMs) {
      nextCuts.push({
        ...cut,
        end_time_ms: startMs,
      });
    }

    // Right remainder.
    if (cutEnd > endMs) {
      nextCuts.push({
        ...cut,
        id: cutStart < startMs ? nanoid() : cut.id,
        start_time_ms: endMs,
      });
    }
  }

  return sortCuts(nextCuts).filter(cut =>
    isValidRange(cut.start_time_ms, cut.end_time_ms),
  );
};

/**
 * Restores a deleted gap by merging two cuts adjacent to the gap.
 */
export const restoreCutFromCuts = (
  cuts: AdvancedEditCut[],
  gapStartMs: number,
  gapEndMs: number,
): AdvancedEditCut[] => {
  if (gapStartMs > gapEndMs) {
    return sortCuts(cuts);
  }

  const orderedCuts = sortCuts(cuts);
  const leftIndex = orderedCuts.findIndex(
    cut => cut.end_time_ms === gapStartMs,
  );
  if (leftIndex < 0 || leftIndex + 1 >= orderedCuts.length) {
    return orderedCuts;
  }

  const leftCut = orderedCuts[leftIndex];
  const rightCut = orderedCuts[leftIndex + 1];
  if (rightCut.start_time_ms !== gapEndMs) {
    return orderedCuts;
  }

  const mergedCut: AdvancedEditCut = {
    ...leftCut,
    end_time_ms: rightCut.end_time_ms,
  };

  return [
    ...orderedCuts.slice(0, leftIndex),
    mergedCut,
    ...orderedCuts.slice(leftIndex + 2),
  ];
};

/**
 * Splits the cut that contains `timeMs` into two cuts.
 */
export const splitCutAtTime = (
  cuts: AdvancedEditCut[],
  timeMs: number,
): AdvancedEditCut[] => {
  const orderedCuts = sortCuts(cuts);
  const splitIndex = orderedCuts.findIndex(
    cut => cut.start_time_ms < timeMs && timeMs < cut.end_time_ms,
  );

  if (splitIndex < 0) {
    return orderedCuts;
  }

  const target = orderedCuts[splitIndex];
  const leftCut: AdvancedEditCut = {
    ...target,
    end_time_ms: timeMs,
  };
  const rightCut: AdvancedEditCut = {
    ...target,
    id: nanoid(),
    start_time_ms: timeMs,
  };

  return [
    ...orderedCuts.slice(0, splitIndex),
    leftCut,
    rightCut,
    ...orderedCuts.slice(splitIndex + 1),
  ];
};

/**
 * Trims the start boundary of a cut.
 */
export const trimCutStart = (
  cuts: AdvancedEditCut[],
  cutId: string,
  newStartMs: number,
): AdvancedEditCut[] => {
  const orderedCuts = sortCuts(cuts);
  const cutIndex = orderedCuts.findIndex(cut => cut.id === cutId);
  if (cutIndex < 0) {
    return orderedCuts;
  }

  const nextCuts = [...orderedCuts];
  const target = nextCuts[cutIndex];
  if (newStartMs >= target.end_time_ms) {
    return orderedCuts;
  }

  // Inward trim (right)
  if (newStartMs >= target.start_time_ms) {
    nextCuts[cutIndex] = {
      ...target,
      start_time_ms: newStartMs,
    };
    return finalizeCuts(nextCuts);
  }

  // Outward extend (left): eat previous cuts as needed.
  nextCuts[cutIndex] = {
    ...target,
    start_time_ms: newStartMs,
  };

  let cursor = cutIndex - 1;
  while (cursor >= 0) {
    const previous = nextCuts[cursor];

    if (previous.end_time_ms <= newStartMs) {
      break;
    }

    // Fully eaten previous segment.
    if (previous.start_time_ms >= newStartMs) {
      nextCuts.splice(cursor, 1);
      cursor -= 1;
      continue;
    }

    // Partially eaten previous segment.
    nextCuts[cursor] = {
      ...previous,
      end_time_ms: newStartMs,
    };
    break;
  }

  return finalizeCuts(nextCuts);
};

/**
 * Trims the end boundary of a cut.
 */
export const trimCutEnd = (
  cuts: AdvancedEditCut[],
  cutId: string,
  newEndMs: number,
): AdvancedEditCut[] => {
  const orderedCuts = sortCuts(cuts);
  const cutIndex = orderedCuts.findIndex(cut => cut.id === cutId);
  if (cutIndex < 0) {
    return orderedCuts;
  }

  const nextCuts = [...orderedCuts];
  const target = nextCuts[cutIndex];
  if (newEndMs <= target.start_time_ms) {
    return orderedCuts;
  }

  // Inward trim (left)
  if (newEndMs <= target.end_time_ms) {
    nextCuts[cutIndex] = {
      ...target,
      end_time_ms: newEndMs,
    };
    return finalizeCuts(nextCuts);
  }

  // Outward extend (right): eat next cuts as needed.
  nextCuts[cutIndex] = {
    ...target,
    end_time_ms: newEndMs,
  };

  const cursor = cutIndex + 1;
  while (cursor < nextCuts.length) {
    const following = nextCuts[cursor];

    if (following.start_time_ms >= newEndMs) {
      break;
    }

    // Fully eaten following segment.
    if (following.end_time_ms <= newEndMs) {
      nextCuts.splice(cursor, 1);
      continue;
    }

    // Partially eaten following segment.
    nextCuts[cursor] = {
      ...following,
      start_time_ms: newEndMs,
      transition: { type: 'jump_cut' },
    };
    break;
  }

  return finalizeCuts(nextCuts);
};
